import { useHandler } from "@/hooks";
import { useShallow } from "zustand/react/shallow";
import type { FpFileCreated, FpFileUpdated } from "../../../schemas/codegen";
import type {
  FpFilesystemSnapshotCreated,
  FpFilesystemSnapshotsUpdated,
} from "../../../schemas/filesystem";
import { useFilesystemStore } from "../../state/stores";

/**
 * Hook for synchronizing WebSocket filesystem events with the filesystem store.
 *
 * This hook handles:
 * - File updates for staged files in the filesystem store (streaming support)
 * - Snapshot list updates when snapshots are created or modified
 * - Individual snapshot creation events with file pre-population
 *
 * @returns Object containing filesystem event handlers for WebSocket integration
 */
export function useFilesystemSync() {
  const { upsertStagedFile, setSnapshots, upsertSnapshotFiles } =
    useFilesystemStore(
      useShallow((state) => ({
        upsertStagedFile: state.upsertStagedFile,
        setSnapshots: state.setSnapshots,
        upsertSnapshotFiles: state.upsertSnapshotFiles,
      })),
    );

  const handleFileCreated = useHandler((event: FpFileCreated) => {
    const { file } = event;

    // Upsert the file to staged files with processing status
    upsertStagedFile({
      path: file.filename,
      content: file.content ?? "",
      status: file.status,
    });
  });

  const handleFileUpdated = useHandler((event: FpFileUpdated) => {
    const { file } = event;

    // Upsert the file to staged files with processing status
    upsertStagedFile({
      path: file.filename,
      content: file.content ?? "",
      status: file.status ?? "processing",
    });
  });

  const handleSnapshotsUpdated = useHandler(
    (event: FpFilesystemSnapshotsUpdated) => {
      // Map server snapshots to UI format - createdAt is already an ISO string from server
      const uiSnapshots = event.snapshots.map((snapshot) => ({
        id: snapshot.id,
        label: snapshot.label,
        createdAt: snapshot.createdAt,
        fileCount: snapshot.fileCount,
        lamportId: snapshot.lamportId,
      }));
      setSnapshots(uiSnapshots);
    },
  );

  const handleSnapshotCreated = useHandler(
    (event: FpFilesystemSnapshotCreated) => {
      const { snapshot } = event;

      // Pre-populate files for the new snapshot
      upsertSnapshotFiles(snapshot.id, snapshot.files);

      // Also update the snapshots list to include the new one
      // Note: This is optional since handleSnapshotsUpdated will also be called,
      // but it provides immediate feedback
      const currentSnapshots = useFilesystemStore.getState().snapshots;
      const newSnapshot = {
        id: snapshot.id,
        label: snapshot.label,
        createdAt: snapshot.createdAt,
        fileCount: snapshot.files.length,
        lamportId: snapshot.lamportId,
      };

      // Add or update the snapshot in the list
      const existingIndex = currentSnapshots.findIndex(
        (s) => s.id === snapshot.id,
      );
      if (existingIndex >= 0) {
        const updatedSnapshots = [...currentSnapshots];
        updatedSnapshots[existingIndex] = newSnapshot;
        setSnapshots(updatedSnapshots);
      } else {
        setSnapshots([...currentSnapshots, newSnapshot]);
      }
    },
  );

  return {
    handleFileCreated,
    handleFileUpdated,
    handleSnapshotsUpdated,
    handleSnapshotCreated,
  };
}
